// src/pages/EditScheme.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import API from "../api/api";
import Swal from "sweetalert2";
import "./AdminDashboard.css";
import statesData from "../data/statesData";

// =========================================================
// MULTI-SELECT DROPDOWN STATES SELECTOR
// =========================================================
function StatesSelector({ value, onChange }) {
  const allStates = useMemo(
    () => ["All India", ...statesData.map((s) => s.name)],
    []
  );

  const [selected, setSelected] = useState(value || ["All India"]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setSelected(value && value.length ? value : ["All India"]);
  }, [value]);

  const toggleState = (state) => {
    if (state === "All India") {
      setSelected(["All India"]);
      onChange(["All India"]);
      return;
    }

    let updated = selected.includes("All India")
      ? []
      : [...selected];

    if (updated.includes(state)) {
      updated = updated.filter((s) => s !== state);
    } else {
      updated.push(state);
    }

    if (updated.length === 0) updated = ["All India"];

    setSelected(updated);
    onChange(updated);
  };

  const filtered = allStates.filter((s) =>
    s.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="mb-3 position-relative">
      <label className="form-label fw-semibold">Applicable States</label>

      <div
        className="form-control d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen(!open)}
      >
        <span className="text-muted">
          {selected.length > 3
            ? `${selected.length} states selected`
            : selected.join(", ")}
        </span>
        <i className={`bi bi-chevron-${open ? "up" : "down"}`}></i>
      </div>

      {open && (
        <div
          className="dropdown-menu show w-100 p-2 mt-1 shadow-sm"
          style={{ maxHeight: 300, overflowY: "auto" }}
        >
          <input
            type="text"
            className="form-control mb-2"
            placeholder="Search state..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {filtered.map((state) => (
            <label
              key={state}
              className="dropdown-item d-flex align-items-center"
            >
              <input
                type="checkbox"
                checked={selected.includes(state)}
                onChange={() => toggleState(state)}
                className="form-check-input me-2"
              />
              {state}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// =========================================================
// MAIN EDIT PAGE
// =========================================================
export default function EditScheme() {
  const { id } = useParams();
  const [scheme, setScheme] = useState(null);
  const [mainCategory, setMainCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedStates, setSelectedStates] = useState(["All India"]);

  // Load scheme data
  useEffect(() => {
    const fetchScheme = async () => {
      try {
        const res = await API.get(`/schemes/${id}`);
        const data = res.data?.data || res.data;

        setScheme(data);
        setMainCategory(data.mainCategory || "");
        setCategories(data.categories || []);
        setSelectedStates(
          Array.isArray(data.states) && data.states.length
            ? data.states
            : ["All India"]
        );
      } catch (err) {
        Swal.fire("Error", "Failed to load scheme details", "error");
      }
    };
    fetchScheme();
  }, [id]);

  // Category handlers
  const handleCategoryChange = (i, field, val) => {
  setCategories((prev) =>
    prev.map((cat, index) =>
      index === i
        ? { ...cat, [field]: val } // update only this category
        : cat                       // keep others as they are
    )
  );
};


  const handleSubChange = (ci, si, field, val) => {
  setCategories((prev) =>
    prev.map((cat, index) => {
      if (index !== ci) return cat;

      const newSubs = cat.subcategories.map((sub, subIndex) =>
        subIndex === si
          ? { ...sub, [field]: val } // update only this subcategory
          : sub
      );

      return { ...cat, subcategories: newSubs };
    })
  );
};


  const addCategory = () =>
    setCategories([
      ...categories,
      { name: "", subcategories: [{ name: "", applyLink: "", youtubeLink: "" }] },
    ]);

const deleteCategory = (i) => {
  setCategories((prev) => prev.filter((_, idx) => idx !== i));
};

  const addSubcategory = (ci) => {
    const updated = [...categories];
    updated[ci].subcategories.push({
      name: "",
      applyLink: "",
      youtubeLink: "",
    });
    setCategories(updated);
  };

  const deleteSubcategory = (ci, si) => {
  setCategories((prev) =>
    prev.map((cat, index) => {
      if (index !== ci) return cat;

      return {
        ...cat,
        subcategories: cat.subcategories.filter((_, idx) => idx !== si),
      };
    })
  );
};


  // SAVE CHANGES
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        mainCategory,
        categories,
        states: selectedStates.length ? selectedStates : ["All India"],
      };

      await API.put(`/schemes/${id}`, payload);

      Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "Scheme updated successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      window.history.back();
    } catch (err) {
      Swal.fire("Error", "Failed to update scheme", "error");
    }
  };

  if (!scheme)
    return (
      <div className="dashboard-wrapper">
        <div className="dashboard-card text-center p-5">
          <h5>Loading...</h5>
        </div>
      </div>
    );

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-card shadow-lg">
        <h3 className="dashboard-title mb-3 text-center text-primary">
          ✏️ Edit Card
        </h3>

        <form onSubmit={handleSubmit}>
          {/* Main Category */}
          <input
            className="form-control input-box mb-4"
            placeholder="Main Category"
            value={mainCategory}
            onChange={(e) => setMainCategory(e.target.value)}
            required
          />

          {/* ⭐ NEW STATE SELECTOR */}
          <StatesSelector
            value={selectedStates}
            onChange={(arr) => setSelectedStates(arr)}
          />

          {/* Categories */}
          {categories.map((cat, ci) => (
            <div key={ci} className="category-box mb-4 p-3">
              <div className="d-flex justify-content-between mb-2">
                <h6 className="fw-bold text-primary">Service Type {ci + 1}</h6>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => deleteCategory(ci)}
                >
                  Delete
                </button>
              </div>

              <input
                className="form-control mb-2"
                placeholder="Category Name"
                value={cat.name}
                onChange={(e) =>
                  handleCategoryChange(ci, "name", e.target.value)
                }
                required
              />

              {cat.subcategories.map((sub, si) => (
                <div key={si} className="subcategory-box mb-3 p-2">
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-secondary">{ci + 1}.{si + 1}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteSubcategory(ci, si)}
                    >
                      Remove
                    </button>
                  </div>

                  <input
                    className="form-control mb-1"
                    placeholder="Subcategory Name"
                    value={sub.name}
                    onChange={(e) =>
                      handleSubChange(ci, si, "name", e.target.value)
                    }
                    required
                  />
                  <input
                    className="form-control mb-1"
                    placeholder="Apply Link"
                    value={sub.applyLink}
                    onChange={(e) =>
                      handleSubChange(ci, si, "applyLink", e.target.value)
                    }
                  />
                  <input
                    className="form-control mb-1"
                    placeholder="YouTube Link"
                    value={sub.youtubeLink}
                    onChange={(e) =>
                      handleSubChange(ci, si, "youtubeLink", e.target.value)
                    }
                  />
                </div>
              ))}

              <button
                type="button"
                className="btn btn-sm btn-outline-success"
                onClick={() => addSubcategory(ci)}
              >
                + Add Service Action

              </button>
            </div>
          ))}

          <button
            type="button"
            className="btn btn-outline-primary mb-3"
            onClick={addCategory}
          >
            + Add Service Type
          </button>

          <div className="text-center">
            <button className="btn btn-success me-2" type="submit">
              Save Changes
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => window.history.back()}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
